import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, RotateCcw, Trash2, Clock } from "lucide-react";

function timeLeft(deletedAtIso) {
  try {
    const deletedAt = new Date(deletedAtIso);
    const expiresAt = new Date(deletedAt.getTime() + 24 * 60 * 60 * 1000);
    const ms = expiresAt - new Date();
    if (ms <= 0) return "Expiring…";
    const hours = Math.floor(ms / 3600000);
    const mins = Math.floor((ms % 3600000) / 60000);
    if (hours > 0) return `${hours}h ${mins}m left`;
    return `${mins}m left`;
  } catch { return ""; }
}

export default function Trash() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tasks/deleted");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const restore = async (task) => {
    setBusy(true);
    try {
      await api.post(`/tasks/${task.id}/restore`);
      setItems((p) => p.filter((x) => x.id !== task.id));
      toast.success(`Restored "${task.title}"`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const restoreAll = async () => {
    if (items.length === 0) return;
    if (!window.confirm(`Restore all ${items.length} deleted tasks?`)) return;
    setBusy(true);
    try {
      await api.post(`/tasks/bulk-restore`, { task_ids: items.map((x) => x.id) });
      toast.success(`Restored ${items.length} task${items.length === 1 ? "" : "s"}`);
      setItems([]);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 py-8">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4"
          data-testid="trash-back-link"
        >
          <ArrowLeft size={14} /> Back
        </button>
        <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
          <div>
            <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
              Recently deleted
            </h1>
            <p className="text-slate-500 mt-2">
              Tasks are kept here for <b>24 hours</b> after deletion. After that they are removed permanently.
            </p>
          </div>
          {items.length > 0 && (
            <Button variant="outline" onClick={restoreAll} disabled={busy} data-testid="trash-restore-all-btn">
              <RotateCcw size={14} className="mr-1.5" /> Restore all ({items.length})
            </Button>
          )}
        </div>

        {loading ? (
          <p className="text-slate-500 text-sm mt-6">Loading…</p>
        ) : items.length === 0 ? (
          <Card className="p-10 rounded-md border-slate-200 shadow-sm text-center mt-6" data-testid="trash-empty">
            <Trash2 size={32} className="text-slate-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Trash is empty</h3>
            <p className="text-slate-500 mt-1 text-sm">Deleted tasks will appear here for 24 hours.</p>
            <Link to="/" className="inline-block mt-4"><Button variant="outline">Back to dashboard</Button></Link>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-6" data-testid="trash-list">
            {items.map((t) => (
              <Card key={t.id} className="p-4 rounded-md border-slate-200 shadow-sm" data-testid={`trash-${t.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">{t.title}</div>
                    <div className="text-xs text-slate-500 mt-1 truncate">
                      {t.assignee_name} · {t.assignee_email}
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="outline" className="text-[10px] text-slate-600 border-slate-300">
                        <Clock size={10} className="mr-1" /> {timeLeft(t.deleted_at)}
                      </Badge>
                      {t.priority && (
                        <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
                          {t.priority}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => restore(t)} disabled={busy} data-testid={`trash-restore-${t.id}`}>
                    <RotateCcw size={12} className="mr-1" /> Restore
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
